/**
 * Subscription Tier Mapping Utilities
 *
 * Maps monday.com subscription data to our internal tier system.
 * This is a shared utility to ensure consistent tier mapping across the app.
 *
 * Future-proof design:
 * - Handles unknown plan IDs gracefully
 * - Supports plan name changes from monday.com
 * - Defaults to 'free' for safety
 * - Logs warnings for unknown plans (for monitoring)
 */

export type SubscriptionTier = 'free' | 'pro';

export interface MondaySubscriptionData {
  plan_id?: string;
  tier?: string;
  period?: string;
  version?: number;
  is_trial?: boolean;
}

// Allow any object with optional plan_id and tier fields
export type SubscriptionInput = {
  plan_id?: string;
  tier?: string;
} | null | undefined;

/**
 * Map monday.com subscription to our tier system
 *
 * Strategy (in order of reliability):
 * 1. Explicit plan_id mapping (most reliable)
 * 2. Fallback to tier name matching (handles renames)
 * 3. Default to 'free' (safest for unknown plans)
 *
 * @param subscription - monday.com subscription data
 * @returns One of: 'free' | 'pro'
 *
 * @example
 * ```ts
 * const tier = getSubscriptionTier({ plan_id: 'pro_tier_16' })
 * // Returns: 'pro'
 *
 * const tier = getSubscriptionTier({ plan_id: 'new_future_plan' })
 * // Returns: 'free' (with console warning)
 * ```
 */
export function getSubscriptionTier(
  subscription?: SubscriptionInput
): SubscriptionTier {
  // No subscription data = free tier
  if (!subscription) return 'free';

  // Strategy 1: Explicit plan_id mapping (most reliable)
  // Based on monday.com's documented plan IDs as of January 2025
  // Source: https://developer.monday.com/api-reference/docs/subscriptions
  const planIdMapping: Record<string, SubscriptionTier> = {
    // Free tier (basic/standard plans)
    'free': 'free',
    'individual': 'free',
    'trial': 'free',
    'basic': 'free',
    'basic_tier_1': 'free',
    'basic_tier_5': 'free',
    'standard': 'free',
    'standard_tier_10': 'free',

    // Pro tier (professional/enterprise plans)
    'professional': 'pro',
    'pro': 'pro',
    'pro_tier_16': 'pro',
    'enterprise': 'pro',
    'premium': 'pro',
    'business': 'pro',

    // TaskLoops App Plan IDs (from monday-app-manifest.json)
    'professional_monthly': 'pro',
    'professional_yearly': 'pro',
  };

  if (subscription.plan_id && planIdMapping[subscription.plan_id]) {
    return planIdMapping[subscription.plan_id] as SubscriptionTier;
  }

  // Strategy 2: Fallback to tier name matching (handles renames)
  if (subscription.tier) {
    const tierLower = subscription.tier.toLowerCase();

    // Pro tier keywords
    if (
      tierLower.includes('enterprise') ||
      tierLower.includes('premium') ||
      tierLower.includes('pro') ||
      tierLower.includes('professional') ||
      tierLower.includes('business')
    ) {
      return 'pro';
    }

    // Free tier keywords (basic/standard now map to free)
    if (
      tierLower.includes('free') ||
      tierLower.includes('individual') ||
      tierLower.includes('trial') ||
      tierLower.includes('basic') ||
      tierLower.includes('standard')
    ) {
      return 'free';
    }
  }

  // Strategy 3: Default to 'free' for unknown plans (safest)
  // This ensures the app doesn't break when monday.com introduces new plans
  console.warn(
    '[SubscriptionTier] Unknown monday.com subscription plan:',
    JSON.stringify(subscription, null, 2)
  );
  console.warn(
    '[SubscriptionTier] Defaulting to "free" tier. Please update plan mapping if needed.'
  );

  // Important: Rate limiting is per-organization regardless of tier,
  // so misclassification only affects UI features, not core functionality
  return 'free';
}

/**
 * Check if a monday.com account is pinned to Pro tier via environment variable.
 * Pinned accounts always get Pro tier regardless of billing state.
 * This survives uninstall/reinstall cycles and blocks webhook-driven downgrades.
 *
 * Set PINNED_PRO_ACCOUNT_IDS as a comma-separated list of monday account IDs.
 * Remove when real marketplace monetization is live.
 */
export function isPinnedProAccount(accountId: number): boolean {
  const pinned = process.env.PINNED_PRO_ACCOUNT_IDS || '';
  return pinned.split(',').map(id => id.trim()).filter(Boolean).includes(String(accountId));
}

/**
 * Get tier-specific feature flags
 *
 * @param tier - Subscription tier
 * @returns Feature availability flags
 *
 * @deprecated Use SUBSCRIPTION_LIMITS from '@/lib/constants/subscription-limits' directly
 * This function is maintained for backward compatibility only.
 */
export function getTierFeatures(tier: SubscriptionTier) {
  // Import moved to runtime to avoid circular dependency
  const { SUBSCRIPTION_LIMITS } = require('@/lib/constants/subscription-limits');
  return SUBSCRIPTION_LIMITS[tier];
}
